import { RouteLocationNormalized, RouteMeta } from 'vue-router'

import {
  AppNavigationGuard,
  AppRouteMeta,
  createMfWrapper,
} from '@sennder/shell-utilities'
import router, { INITIAL_ROUTE_PATH } from '@/router'
import {
  getMicrofrontendData,
  getStateCallbacks,
  getStateProviders,
} from '@/store'
import { logger } from '@/services/logger/loggers'
import { AppRoute, routes, redirects } from '@/config/routes'
import { loadUserData } from '@/store/loadUserData'
import { auth } from '@/modules/auth'
import { i18n } from '@/services/i18n'

export const getMfContext = async (npmName: string) => {
  return {
    data: getMicrofrontendData(npmName),
    callbacks: getStateCallbacks(),
    providers: {
      ...getStateProviders(),
    },
  }
}

/**
 * Micro-frontends with routes
 */

export const addAppRoute = (routePath: AppRoute, routeMeta: AppRouteMeta) => {
  const component = async () =>
    createMfWrapper({
      id: routeMeta.name,
      renderer: routeMeta.renderer,
      hooks: {
        failure: logger.error,
      },
      router,
    })

  const guards = routeMeta.guards || []

  router.addRoute({
    path: routePath,
    name: routeMeta.name,
    component,
    meta: {
      layout: routeMeta.fullscreenLayout ? 'AppLayoutAuth' : 'AppLayoutDefault',
      public: routeMeta.public,
      guards,
      title: `navigation.item.${routeMeta.name}`,
    },
    children: [
      {
        path: ':catchAll(.*)',
        meta: {
          guards,
        },
        component,
      },
    ],
  })
}

export const registerRoutes = () => {
  initRouteGuards()

  Object.entries(routes).forEach(([path, route]) => {
    addAppRoute(path as AppRoute, route)
  })

  Object.entries(redirects).forEach(([path, redirect]) => {
    router.addRoute({
      path,
      redirect,
    })
  })

  router.addRoute({
    path: '/:catchAll(.*)',
    name: 'not-found',
    component: () => import('../NotFound.vue'),
    meta: {
      title: 'navigation.item.not-found',
    },
  })

  router.addRoute({
    path: '/',
    redirect: { path: INITIAL_ROUTE_PATH },
  })
}

export const initRouteGuards = () => {
  router.beforeEach(async (to, from) => {
    await checkUserDataLoaded(from, to)
    return routeNavigationGuard(to, from)
  })

  router.beforeResolve((to) => {
    const { name, meta } = to
    setPageTitle(String(name), meta)
  })
}

const setPageTitle = (name: string, meta: RouteMeta): void => {
  const pageName =
    (meta.title && i18n.global.t(meta.title)) ||
    name.charAt(0).toUpperCase() + name.slice(1)
  document.title = `${pageName} |  sennTMS - sennder`
}

const LOGIN_ROUTE_NAME = routes['/login'].name

async function checkUserDataLoaded(
  from: RouteLocationNormalized,
  to: RouteLocationNormalized
) {
  // If we are navigating from login page to another page, we need to load user data
  if (from.name === LOGIN_ROUTE_NAME && to.name !== LOGIN_ROUTE_NAME) {
    await loadUserData(true)
  }
}

const isAuthenticatedGuard: AppNavigationGuard = async (
  to: RouteLocationNormalized
) => {
  if (await auth.value?.isAuthenticated()) {
    return true
  } else {
    return {
      name: LOGIN_ROUTE_NAME,
      query: { redirectTo: encodeURIComponent(to.fullPath) },
    }
  }
}

const isNotAuthenticatedGuard: AppNavigationGuard = async () => {
  if (await auth.value?.isAuthenticated()) {
    return { path: INITIAL_ROUTE_PATH }
  } else {
    return true
  }
}

export const routeNavigationGuard: AppNavigationGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) => {
  const guards = [
    to.meta.public ? isNotAuthenticatedGuard : isAuthenticatedGuard,
    ...(to.meta.guards || []),
  ]
  if (guards.length === 0) {
    return true
  }

  for (const guard of guards) {
    const result = await guard(to, from)
    if (
      typeof result === 'object' ||
      typeof result === 'string' ||
      result === false
    ) {
      return result
    }
  }

  return true
}
